import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SectionHeader from '../ui/SectionHeader';
import AnimatedCard from '../ui/AnimatedCard';
import MotionReveal from '../ui/MotionReveal';
import { impactOutcomes } from '../../data/projectsData';
import { sectionCopy } from '../../data/portfolioData';
import { slideUp } from '../../motion/variants';

const Impact = () => {
  const [current, setCurrent] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const copy = sectionCopy.impact;

  useEffect(() => {
    if (!autoPlay) return undefined;
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % impactOutcomes.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [autoPlay]);

  const item = impactOutcomes[current];

  const goTo = (index) => {
    setCurrent(index);
    setAutoPlay(false);
  };

  return (
    <section id="impact" className="section-padding bg-ant-bg text-ant-fg border-t border-ant-line-soft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          label={copy.label}
          titleLine1={copy.titleLine1}
          titleLine2={copy.titleLine2}
          subtitle={copy.subtitle}
          align="center"
          className="text-center mx-auto"
        />

        <MotionReveal>
          <AnimatedCard className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 sm:p-8 lg:p-12 mb-10 hover:border-white/[0.14] overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={item.id}
                variants={slideUp}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="ant-dot" />
                    <span className="font-mono text-[10px] tracking-widest text-ant-green uppercase">
                      {copy.liveLabel}
                    </span>
                  </div>

                  <blockquote className="text-white/75 text-lg sm:text-xl leading-relaxed mb-6 font-medium">
                    {item.highlight}
                  </blockquote>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {item.metrics.map((metric) => (
                      <span
                        key={metric}
                        className="px-3 py-1.5 rounded-full border border-ant-green/20 bg-ant-green/10 font-mono text-xs text-ant-green"
                      >
                        {metric}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm text-white/35 font-mono">
                    <span>{item.project}</span>
                    <span className="text-white/15">·</span>
                    <span>{item.client}</span>
                    <span className="text-white/15">·</span>
                    <span>{item.duration}</span>
                  </div>
                </div>

                <div className="rounded-xl border border-white/[0.06] bg-ant-bg-2 p-5 lg:w-64 shrink-0">
                  <p className="font-mono text-[10px] text-white/25 mb-3 uppercase tracking-widest">
                    {copy.categoryLabel}
                  </p>
                  <p className="text-white/80 font-medium mb-4">{item.category}</p>
                  {item.liveDemo && (
                    <a
                      href={item.liveDemo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-ant-gold hover:text-ant-gold/80 transition-colors"
                    >
                      {copy.viewLive}
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </AnimatedCard>
        </MotionReveal>

        <div className="flex items-center justify-center gap-4 mb-12">
          <motion.button
            whileTap={{ scale: 0.92 }}
            onClick={() => goTo((current - 1 + impactOutcomes.length) % impactOutcomes.length)}
            className="p-2.5 rounded-full border border-white/10 bg-white/[0.04] hover:border-white/20 transition-all"
            aria-label="Previous outcome"
          >
            <svg className="w-4 h-4 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </motion.button>

          <div className="flex gap-2">
            {impactOutcomes.map((outcome, index) => (
              <motion.button
                key={outcome.id}
                whileTap={{ scale: 0.9 }}
                onClick={() => goTo(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all nav-dot ${
                  index === current ? 'bg-ant-green scale-125 active' : 'bg-white/20 hover:bg-white/30'
                }`}
                aria-label={`View ${outcome.project}`}
              />
            ))}
          </div>

          <motion.button
            whileTap={{ scale: 0.92 }}
            onClick={() => goTo((current + 1) % impactOutcomes.length)}
            className="p-2.5 rounded-full border border-white/10 bg-white/[0.04] hover:border-white/20 transition-all"
            aria-label="Next outcome"
          >
            <svg className="w-4 h-4 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </motion.button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {impactOutcomes.map((outcome, index) => (
            <MotionReveal key={outcome.id} delay={index * 80}>
              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => goTo(index)}
                className={`w-full text-left rounded-xl border p-5 transition-all ${
                  index === current
                    ? 'border-ant-gold/30 bg-ant-gold/5'
                    : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12]'
                }`}
              >
                <p className="font-mono text-[10px] text-ant-gold/70 mb-2">{outcome.category}</p>
                <h3 className="text-sm font-semibold text-white mb-2">{outcome.project}</h3>
                <p className="text-xs text-white/40 line-clamp-2">{outcome.highlight}</p>
              </motion.button>
            </MotionReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Impact;